import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { XIcon as X, CircleNotchIcon as Spinner, ArrowSquareOutIcon as Out } from "@phosphor-icons/react";
import { getFieldReference } from "@/lib/api";
import { originLabel } from "@/lib/fieldDependencyConstants";
import { blockedByAmbiguousFormat, inheritedViaBinding, inheritedViaManualBinding } from "@/lib/screenBindings";
import TypeBadge from "@/components/fielddeps/TypeBadge";
import { fmtDateTime } from "@/lib/provenanceDisplay";
import ConfidenceBadge from "@/components/fielddeps/ConfidenceBadge";
import ValidationBadge from "@/components/validation/ValidationBadge";

function Row({ label, value, mono = false, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-[var(--ml-border)] last:border-b-0 text-xs" data-testid={testid}>
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] shrink-0 pt-0.5">{label}</span>
      <span className={`text-right break-words min-w-0 ${mono ? "ml-mono" : ""}`}>{value ?? <span className="text-[var(--ml-text-secondary)]/60">—</span>}</span>
    </div>
  );
}

/** Right-hand drawer: every column of one field reference + its evidence chain (Iter 41). */
export default function FieldRefDetail({ workspaceId, referenceId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!referenceId) { setData(null); return; }
    let alive = true;
    setLoading(true);
    getFieldReference(workspaceId, referenceId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [workspaceId, referenceId]);

  useEffect(() => {
    if (!referenceId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [referenceId, onClose]);

  if (!referenceId) return null;
  const r = data?.reference;
  const b = data?.batch;
  return (
    <aside className="ml-card p-4 lg:sticky lg:top-24 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="fd-detail">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Field reference #{referenceId}</div>
          {r && <h2 className="text-lg font-semibold ml-mono mt-0.5" data-testid="fd-detail-field">{r.field_name}</h2>}
        </div>
        <button type="button" onClick={onClose} className="p-1 hover:bg-[var(--ml-surface)]" aria-label="Close" data-testid="fd-detail-close">
          <X size={14} weight="bold" />
        </button>
      </div>
      {loading || !r ? (
        <div className="text-sm text-[var(--ml-text-secondary)] inline-flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <ConfidenceBadge confidence={r.confidence} ambiguous={r.ambiguous} testid="fd-detail-confidence" />
            <TypeBadge type={r.reference_type} testid="fd-detail-type" />
            <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--ml-border)] text-[var(--ml-text-secondary)]">{originLabel(r.origin)}</span>
          </div>
          {r.ambiguous && (
            <p className="text-xs px-3 py-2" style={{ background: "#fff8e1", color: "#8a6d00" }} data-testid="fd-detail-ambiguous-note">
              {r.display_file
                ? "Ambiguous database mapping: this screen field only shares its name with a database field. Nothing in the DDS (REF / REFFLD) proves the link, so no lineage is claimed."
                : "Several files carry this field name. Lineage is deliberately left unresolved — pick the file in the Field impact search or import a narrower DSPPGMREF / parse the program."}
            </p>
          )}
          {r.display_file && (
            <div className="text-xs px-3 py-2 border-l-2" style={{ borderColor: "#9f1853", background: "#fff0f7" }} data-testid="fd-detail-screen">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Display-file field reference</div>
              <Row label="Display file" value={r.display_file} mono testid="fd-detail-display-file" />
              <Row label="Record format" value={r.record_format} mono testid="fd-detail-record-format" />
              <Row label="Source member" value={r.source_member} mono testid="fd-detail-source-member" />
              <Row label="DB mapping" value={r.file_name ? `${r.file_library ? r.file_library + "/" : ""}${r.file_name} · proven by REF / REFFLD` : (r.ambiguous ? "ambiguous (same-name only)" : "none claimed")} testid="fd-detail-db-mapping" />
              <Row label="Program via" value={inheritedViaManualBinding(r) ? "manual screen format binding — consultant supplied, not parser-proven" : inheritedViaBinding(r) ? "observed screen format binding (EXFMT / WRITE / READ / READC)" : blockedByAmbiguousFormat(r) ? "not inherited — ambiguous format name" : r.program_name ? "single program declaring the display file" : "no program known"} testid="fd-detail-program-via" />
              <div className="flex flex-wrap gap-3 mt-1">
                <Link to={`/workspaces/${workspaceId}/metadata/source-parser?program=${encodeURIComponent(r.display_file)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-detail-dds-facts-link">
                  Open parsed DDS facts <Out size={11} />
                </Link>
                <Link to={`/workspaces/${workspaceId}/metadata/screen-bindings?display_file=${encodeURIComponent(r.display_file)}&record_format=${encodeURIComponent(r.record_format || "")}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-detail-bindings-link">
                  Screen format bindings <Out size={11} />
                </Link>
              </div>
            </div>
          )}
          <div>
            <Row label="Program" value={r.program_name ? `${r.program_library ? r.program_library + "/" : ""}${r.program_name}` : null} mono testid="fd-detail-program" />
            <Row label="Source" value={r.source_file ? `${r.source_file}/${r.source_member || "?"}` : (r.source_member || null)} mono />
            <Row label="File" value={r.file_name ? `${r.file_library ? r.file_library + "/" : ""}${r.file_name}` : (r.display_file ? (r.ambiguous ? "ambiguous database mapping" : "no database mapping") : (r.ambiguous ? "ambiguous" : "unresolved"))} mono testid="fd-detail-file" />
            <Row label="Field id" value={r.field_id} mono testid="fd-detail-field-id" />
            <Row label="Usage" value={r.usage_context} testid="fd-detail-usage" />
            <Row label="Evidence" value={r.evidence_source} mono testid="fd-detail-evidence" />
            <Row label="Fingerprint" value={r.fingerprint?.slice(0, 12)} mono />
            <Row label="Updated" value={fmtDateTime(r.updated_at)} />
          </div>
          {r.source_excerpt && (
            <div>
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Source excerpt</div>
              <pre className="ml-mono text-xs bg-[var(--ml-surface)] p-2 whitespace-pre-wrap break-words" data-testid="fd-detail-excerpt">{r.source_excerpt}</pre>
            </div>
          )}
          {data.business_rule && (
            <div data-testid="fd-detail-rule">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Business rule</div>
              <div className="text-xs flex items-center gap-2 flex-wrap">
                <span className="ml-mono">{data.business_rule.program_name}:{data.business_rule.line_number} · {data.business_rule.rule_kind.toUpperCase()}</span>
                <ValidationBadge state={data.business_rule.validation_state} row={data.business_rule} testid="fd-detail-rule-state" />
                <Link to={`/workspaces/${workspaceId}/business-rules?program=${encodeURIComponent(data.business_rule.program_name)}&field=${encodeURIComponent(r.field_name)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-detail-rule-link">
                  Open rules <Out size={11} />
                </Link>
              </div>
            </div>
          )}
          {data.source_fact && (
            <Row label="Parsed source" value={`${data.source_fact.program_name} (${data.source_fact.source_type}) · ${fmtDateTime(data.source_fact.parsed_at)}`} testid="fd-detail-source-fact" />
          )}
          <div data-testid="fd-detail-provenance">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Provenance</div>
            {b ? (
              <div className="text-xs space-y-0.5">
                <div><span className="ml-mono font-semibold">{b.source_command}</span> · sha {b.file_sha256_short || "Unknown"} · {fmtDateTime(b.imported_at)}</div>
                <Link to={`/workspaces/${workspaceId}/metadata/imports?batch=${b.id}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-detail-batch-link">
                  Import batch {b.id.slice(0, 8)} <Out size={11} />
                </Link>
              </div>
            ) : (
              <div className="text-xs text-[var(--ml-text-secondary)]" data-testid="fd-detail-provenance-unknown">
                {r.origin === "manual" ? "Manual — consultant-authored, no import batch." : "Unknown — parser-derived, no DSPFFD row resolved behind this reference."}
              </div>
            )}
          </div>
          {data.catalog?.length > 0 && (
            <div data-testid="fd-detail-catalog">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Files containing {r.field_name} (DSPFFD)</div>
              <ul className="text-xs space-y-0.5">
                {data.catalog.map((c) => (
                  <li key={c.field_id} className="ml-mono flex items-center justify-between gap-2">
                    <span>{c.library_name}/{c.file_name}{c.key_field ? " · key" : ""}</span>
                    <span className="text-[var(--ml-text-secondary)]">{c.data_type}{c.field_length ? `(${c.field_length})` : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Link to={`/workspaces/${workspaceId}/metadata/field-dependencies?impact=${encodeURIComponent(r.field_name)}`} className="text-xs text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-detail-impact-link">
            Field impact for {r.field_name} <Out size={11} />
          </Link>
        </div>
      )}
    </aside>
  );
}
